/**
 * Promotional popup: when to show it, and when to keep quiet.
 *
 * Timing follows what actually converts rather than what is easiest:
 *   - never straight away; an instant popup is dismissed reflexively and
 *     pushes bounce rate up,
 *   - it opens on whichever comes first: a real dwell (default 7s) or a
 *     meaningful scroll (default 35% of the page),
 *   - on desktop it also opens on exit intent, when the pointer leaves
 *     towards the browser chrome,
 *   - once per session, and once dismissed it stays away for days.
 *
 * It never appears on the cart or checkout, where the shopper already has
 * intent and an interruption only costs money.
 *
 * Memory is localStorage, first-party, no tracking and nothing personal:
 * a single timestamp saying "do not ask again until".
 *
 * Add ?popup=1 to any URL (or #popup) to open it straight away and ignore
 * that memory - the only way to check it on the live site without clearing
 * site data first. A popup opened that way does not write the memory back.
 */

/* The suffix is the memory's version. Bumping it retires every snooze already
   written, which is how a change like this one reaches shoppers who dismissed
   the popup days ago instead of only new visitors. */
const KEY = 'imagine:promo-popup:snooze-until:v2';
const SESSION_KEY = 'imagine:promo-popup:seen';

/**
 * Web storage is not always there to be read. In private mode, in an in-app
 * browser and whenever site data is blocked it can throw - and it throws on
 * the PROPERTY, not just on the call, so `window.sessionStorage` has to sit
 * inside the try too. Every access degrades to "no memory", because a popup
 * that forgets is better than one that never opens.
 */
const storage = (area) => ({
  get(key, fallback = null) {
    try {
      return window[area].getItem(key) ?? fallback;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window[area].setItem(key, value);
    } catch {
      /* nothing to do: the popup simply forgets */
    }
  },
});

const store = storage('localStorage');
const session = storage('sessionStorage');

const root = document.querySelector('[data-promo-popup]');

if (root) {
  const delay = Number(root.dataset.delay) || 7000;
  const scrollTrigger = Number(root.dataset.scroll) || 35;
  const snoozeDays = Number(root.dataset.rememberDays) || 7;
  const convertedDays = Number(root.dataset.rememberDaysConverted) || 30;

  const path = window.location.pathname;
  const onCheckoutPath = /\/(cart|checkout)(\/|$)/.test(path);
  const snoozedUntil = Number(store.get(KEY, 0));
  const snoozed = Number.isFinite(snoozedUntil) && Date.now() < snoozedUntil;
  const seenThisSession = session.get(SESSION_KEY) === '1';

  /** ?popup=1 or #popup: show it now, remember nothing. For checking it live. */
  const forced =
    new URLSearchParams(window.location.search).get('popup') === '1' ||
    window.location.hash === '#popup';

  let opened = false;
  let timer = null;

  const snooze = (days) => {
    if (forced) return;
    store.set(KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  };

  const close = () => {
    root.hidden = true;
    root.classList.remove('promo-popup--open');
    document.documentElement.classList.remove('promo-popup-locked');
    snooze(snoozeDays);
    document.removeEventListener('keydown', onKey);
  };

  function onKey(event) {
    if (event.key === 'Escape') close();
  }

  const open = () => {
    if (opened) return;
    opened = true;
    window.clearTimeout(timer);
    detach();

    root.hidden = false;
    // next frame, so the transition has a start state to animate from
    requestAnimationFrame(() => root.classList.add('promo-popup--open'));
    document.documentElement.classList.add('promo-popup-locked');
    document.addEventListener('keydown', onKey);

    if (!forced) session.set(SESSION_KEY, '1');

    root.querySelector('.promo-popup__cta')?.focus({ preventScroll: true });
  };

  const onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = (window.scrollY / scrollable) * 100;
    if (percent >= scrollTrigger) open();
  };

  const onExitIntent = (event) => {
    if (event.clientY <= 0) open();
  };

  function detach() {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
  }

  if (forced) {
    open();
  } else if (!onCheckoutPath && !snoozed && !seenThisSession) {
    timer = window.setTimeout(open, delay);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Exit intent is a pointer gesture; it has no meaning on touch.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  root.querySelectorAll('[data-promo-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  /* ---------------------------------------------------------------
     Signup. The subscriber goes into the Klaviyo EMAIL list, because
     the account has no registered SMS sender - Klaviyo cannot text
     anyone, not even the opt-in confirmation. The phone is collected
     and stored on the profile anyway, so the SMS audience is being
     built in the meantime and needs no second ask once a sender
     exists.

     The list id in snippets/promo-popup.liquid is REQUIRED.
     /client/subscriptions/ is the public, key-less endpoint behind
     custom signup forms, and it takes no consent object of its own:
     the LIST decides the channel and the opt-in process. That is also
     why a `subscriptions` block on the profile is rejected outright -
     that field belongs to the server-side Subscribe Profiles endpoint,
     which needs a private key and cannot run in a storefront.
     --------------------------------------------------------------- */
  const form = root.querySelector('[data-promo-form]');
  const status = root.querySelector('[data-promo-status]');
  const successText = root.querySelector('[data-promo-success]')?.innerHTML.trim() ?? '';
  const company = root.dataset.klaviyoCompany;
  const list = root.dataset.klaviyoList;

  const say = (message, kind) => {
    if (!status) return;
    status.textContent = message;
    status.dataset.kind = kind;
  };

  /** Good enough: the server is the real validator, this only catches typos. */
  const isEmail = (raw) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(raw).trim());

  /** Israeli mobile numbers, typed the way people actually type them. */
  const toE164 = (raw) => {
    const digits = raw.replace(/[^\d+]/g, '');
    if (/^\+972\d{8,9}$/.test(digits)) return digits;
    if (/^972\d{8,9}$/.test(digits)) return `+${digits}`;
    if (/^0\d{8,9}$/.test(digits)) return `+972${digits.slice(1)}`;
    return null;
  };

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const nameInput = form.querySelector('input[name="name"]');
    const emailInput = form.querySelector('input[name="email"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const consent = form.querySelector('input[name="consent"]');

    const fullName = (nameInput?.value ?? '').trim();
    if (fullName.length < 2) {
      say('נא להזין שם מלא', 'error');
      nameInput?.focus();
      return;
    }

    const email = (emailInput?.value ?? '').trim();
    if (!isEmail(email)) {
      say('נא להזין כתובת מייל תקינה', 'error');
      emailInput?.focus();
      return;
    }

    const phone = toE164(phoneInput?.value ?? '');
    if (!phone) {
      say('נא להזין מספר טלפון נייד תקין', 'error');
      phoneInput?.focus();
      return;
    }
    if (!consent?.checked) {
      say('צריך לאשר קבלת הודעות כדי להצטרף', 'error');
      return;
    }
    if (!company || !list) {
      say('ההרשמה עדיין לא מחוברת. חסרה הגדרת קלביו.', 'error');
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('disabled', '');
    say('רק רגע...', 'pending');

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Members club popup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      phone_number: phone,
                      first_name: fullName.split(/\s+/)[0] || undefined,
                      last_name: fullName.split(/\s+/).slice(1).join(' ') || undefined,
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        }
      );

      if (!response.ok) {
        /* The shopper gets the friendly line below; the reason goes to the
           console, because "it does not work" is not something a Klaviyo
           account id or a revoked key tells you from the outside. */
        console.error('[promo popup] Klaviyo refused the subscription:', response.status, await response.text());
        throw new Error(String(response.status));
      }
      say(successText, 'success');
      snooze(convertedDays);
      /* querySelectorAll, not querySelector: there are three fields now, and
         hiding only the first one left the email and phone boxes on screen
         under the success message. */
      form.querySelectorAll('.promo-popup__input').forEach((el) => el.setAttribute('hidden', ''));
      /* The consent row needs an inline display, not the hidden attribute:
         base.css gives .promo-popup__consent `display: flex`, which outranks
         the browser's own [hidden] rule, so the attribute alone left the
         checkbox sitting under the success message. */
      form.querySelector('.promo-popup__consent')?.style.setProperty('display', 'none');
      button?.setAttribute('hidden', '');
    } catch {
      say('משהו השתבש. אפשר לנסות שוב בעוד רגע.', 'error');
      button?.removeAttribute('disabled');
    }
  });
}
